import api, { SOCKET_BASE_URL } from './api';
import { Notification } from '../types';
import { io, Socket } from 'socket.io-client';

let socket: Socket | null = null;
let subscribers: ((count: number) => void)[] = [];
let currentUnreadCount = 0;
let pollTimer: ReturnType<typeof setInterval> | null = null;

const stopPolling = () => {
  if (pollTimer) {
    clearInterval(pollTimer);
    pollTimer = null;
  }
};

const startPolling = () => {
  if (pollTimer) return;
  pollTimer = setInterval(async () => {
    try {
      const count = await getUnreadCount();
      currentUnreadCount = count;
      notifySubscribers();
    } catch {
      // Polling is a best-effort fallback while the socket reconnects.
    }
  }, 15000);
};

const notifySubscribers = () => {
  subscribers.forEach(sub => sub(currentUnreadCount));
};

const readLatestToken = () =>
  localStorage.getItem('token') || sessionStorage.getItem('token');

export const subscribeToNotifications = (
  userId: string,
  onNewNotification: (notification: Notification) => void,
  onUnreadCountChanged: (count: number) => void
) => {
  subscribers.push(onUnreadCountChanged);

  if (!socket) {
    const token = readLatestToken();
    if (token) {
      socket = io(SOCKET_BASE_URL, {
        path: '/api/socket.io/',
        auth: { token },
        // websocket-first with polling fallback — never force a single transport
        transports: ['websocket', 'polling'],
        // Automatic reconnection with exponential backoff + jitter.
        // The manager keeps retrying forever; the app only degrades to REST
        // polling (below) and never tears the socket down on transient errors.
        reconnection: true,
        reconnectionAttempts: Infinity,
        reconnectionDelay: 1000,
        reconnectionDelayMax: 15000,
        randomizationFactor: 0.5,
        timeout: 15000,
      });

      // Graceful handling: NEVER disconnect here — socket.io-client owns the
      // reconnection loop. Log the cause, refresh the auth token if it was the
      // reason, and keep REST polling alive until the socket reconnects.
      socket.on('connect_error', (error) => {
        console.warn('Notification socket connect error:', error.message);
        const message = error.message || '';
        if (/Authentication error|token/i.test(message) || error instanceof Error) {
          const freshToken = readLatestToken();
          if (freshToken && socket) {
            // Next automatic reconnect attempt authenticates with the new token.
            (socket.io.opts as any).auth = { token: freshToken };
          }
        }
        startPolling();
      });

      socket.on('connect', () => {
        stopPolling();
      });

      socket.on('disconnect', (reason) => {
        // Server-initiated disconnects still reconnect automatically; only a
        // missing token means the session is truly gone.
        if (!readLatestToken()) {
          stopPolling();
        }
      });

      socket.on('new_notification', (notification: Notification) => {
        currentUnreadCount++;
        notifySubscribers();
        onNewNotification(notification);
      });

      socket.on('user_role_updated', (data: { userId?: string; isPromoted?: boolean; isAdmin?: boolean; reason?: string }) => {
        window.dispatchEvent(new CustomEvent('auth-role-updated', { detail: data }));
      });
    }
  }

  // Initial fetch of unread count
  getUnreadCount().then(count => {
    currentUnreadCount = count;
    notifySubscribers();
  }).catch(() => {});

  return () => {
    subscribers = subscribers.filter(sub => sub !== onUnreadCountChanged);
    if (subscribers.length === 0 && socket) {
      stopPolling();
      socket.disconnect();
      socket = null;
    }
  };
};

export const getNotifications = async (page = 1, limit = 20): Promise<{ notifications: Notification[], totalPages: number }> => {
  const response = await api.get(`/notifications?page=${page}&limit=${limit}`);
  return {
    notifications: response.data.notifications,
    totalPages: response.data.pagination.totalPages
  };
};

export const getUnreadCount = async (): Promise<number> => {
  const response = await api.get('/notifications/unread-count');
  return response.data.unreadCount;
};

export const markNotificationAsRead = async (notificationId: string) => {
  await api.patch(`/notifications/${notificationId}/read`);
  currentUnreadCount = Math.max(0, currentUnreadCount - 1);
  notifySubscribers();
};

export const markAllNotificationsAsRead = async () => {
  await api.patch('/notifications/read-all');
  currentUnreadCount = 0;
  notifySubscribers();
};
